import { useEffect, useMemo, useState } from 'react';
import { EyeIcon } from '@heroicons/react/24/outline';
import PhoneInput from 'react-phone-number-input/input';

import BankTransferIcon from 'icons/BankTransferIcon';
import { WithdrawalAccountType } from 'lib/constants';
import { IWithdrawalMethod } from 'types';
import Star from 'icons/Star';
import LoadingSpinner from 'icons/LoadingSpinner';
import Warning from 'icons/Warning';
import PayoutTypeWise from 'icons/PayoutTypeWise';
import PayoutTypePayoneer from 'icons/PayoutTypePayoneer';
import { removeUnderscoreAndCapitalizeFirstLetters } from 'utils/string';
import TheraMenu from 'components/generic-components/menu/Menu';
import MoreButton from 'components/generic-components/menu/MoreButton';
import Button from 'components/app-components/ui/button';
import theraToast from 'components/generic-components/toast/TheraToast';
import WithdrawalMethodDetailsModal from '../modals/WithdrawalMethodDetailsModal';
import { useModal } from 'hooks/useModal';
import { useUser } from 'context/user';
import { useTheraFernClient } from 'hooks/useFern';
import { showIDToCopy, isSuperAdminMode, isSupportAdminMode } from 'utils/user';
import CopyableText from 'components/generic-components/copyableText';

import PayoutTypeDolarapp from 'icons/PayoutTypeDolarapp';
import PayoutTypeTakenos from 'icons/PayoutTypeTakenos';
import PayoutTypeAstropay from 'icons/PayoutTypeAstropay';
import PayoutTypePaypal from 'icons/PayoutTypePaypal';
import CryptoPayMethod from 'icons/CryptoPayMethod';

export const getAccountTypeIcon = (accountType: WithdrawalAccountType, largerIcon = false) => {
  switch (accountType) {
    case WithdrawalAccountType.THERA_WALLET:
      return (
        <span>
          <img
            src={`/images/thera-new-wallet-logo.png`}
            className={` ${largerIcon ? 'h-12 w-12' : 'h-10 w-10'}`}
            alt="thera-wallet-logo"
          />
        </span>
      );
    case WithdrawalAccountType.WISE:
      return <PayoutTypeWise />;
    case WithdrawalAccountType.PAYONEER:
      return <PayoutTypePayoneer />;
    case WithdrawalAccountType.DOLARAPP:
      return <PayoutTypeDolarapp />;
    case WithdrawalAccountType.TAKENOS:
      return <PayoutTypeTakenos />;
    case WithdrawalAccountType.ASTROPAY:
      return <PayoutTypeAstropay />;
    case WithdrawalAccountType.PAYPAL:
      return <PayoutTypePaypal />;
    case WithdrawalAccountType.DLOCAL_STABLECOIN:
      return <CryptoPayMethod />;
    default:
      return <BankTransferIcon />;
  }
};

const WiseWithdrawalMethodItem = ({
  id,
  accountType,
  method,
  isSelected = false,
  isSelectable = false,
  isDisabled = false,
  handleSelectMethod,
  onDelete,
  isPreferred = false,
  setPreferredMethod,
  isSettingPreferred,
  onMakeOffPlatform,
  onConnectPayoneer,
  onMakeOnPlatform,
}: {
  id: string;
  accountType: WithdrawalAccountType;
  method: IWithdrawalMethod;
  isSelected?: boolean;
  isSelectable?: boolean;
  isDisabled?: boolean;
  handleSelectMethod?: (id: string) => void;
  onDelete?: (id: string) => void;
  isPreferred?: boolean;
  setPreferredMethod?: (id: string) => void;
  isSettingPreferred?: string;
  onMakeOffPlatform?: (id: string) => void;
  onConnectPayoneer?: (id: string) => void;
  onMakeOnPlatform?: (id: string) => void;
}) => {
  const withdrawalDetailsModal = useModal();
  const { user } = useUser();
  const { theraFernApi } = useTheraFernClient();
  // Existing Payoneer methods (accountType PAYONEER) can only be paid once their payee is Active in our program.
  // Check the payee status; if it isn't active yet, surface a CTA that opens the "add to our program" connect flow.
  const payoneerEmail = method.details?.payoneerEmail ?? method.details?.email;
  const isPayoneerMethod = method.accountType === WithdrawalAccountType.PAYONEER;
  const [payoneerActive, setPayoneerActive] = useState<boolean | null>(null);
  useEffect(() => {
    if (!isPayoneerMethod || !user?.userID || !payoneerEmail) return;
    let cancelled = false;
    theraFernApi.thera.bank.bankApi
      .getPayoneerPayeeStatus(user.userID, { payoneerEmail })
      .then((res) => {
        if (!cancelled) setPayoneerActive(Boolean(res.registered));
      })
      .catch(() => {
        if (!cancelled) setPayoneerActive(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPayoneerMethod, user?.userID, payoneerEmail, theraFernApi]);
  const needsPayoneerConnect = isPayoneerMethod && (!payoneerEmail || payoneerActive === false);
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    if (!withdrawalDetailsModal.isOpen) setIsHovered(false);
  }, [withdrawalDetailsModal.isOpen]);

  const showWithdrawalMethodIDToCopy = useMemo(() => {
    return showIDToCopy(method);
  }, [method]);

  return (
    <div
      className={`flex w-full items-center justify-between rounded-lg border p-4 transition ${
        isSelected ? 'border-2 border-therablue bg-[#D2D9F440] shadow-md' : 'border-gray-200 bg-white'
      } ${isSelectable && !isDisabled ? 'cursor-pointer' : ''} ${isDisabled ? 'opacity-50' : ''}`}
      key={id}
      role={isSelectable && !isDisabled ? 'button' : undefined}
      onClick={() => isSelectable && !isDisabled && handleSelectMethod(id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <WithdrawalMethodDetailsModal
        open={withdrawalDetailsModal.isOpen}
        onClose={withdrawalDetailsModal.handleClose}
        method={method}
        isPreferred={isPreferred}
      />

      <div className="flex min-w-0 flex-1 items-center">
        <div
          className={`mr-4 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full transition ${
            isSelected ? 'bg-white' : 'bg-neutral-100'
          }`}
        >
          {getAccountTypeIcon(accountType, true)}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium text-neutral-800">
            <span>
              {method.accountType === 'DOLARAPP'
                ? 'DolarApp'
                : method.accountType === 'DLOCAL_STABLECOIN'
                  ? 'Stablecoin'
                  : removeUnderscoreAndCapitalizeFirstLetters(method.accountType)}
            </span>
            <span className="-mt-2">.</span>
            <span>{method.currency?.toUpperCase()}</span>
            {isPreferred && (
              <div className="flex flex-shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-therablue px-3 py-1.5 text-white">
                <Star />
                <span className="ml-2 text-xs font-medium">Preferred Method</span>
              </div>
            )}
          </div>
          {method.accountType !== WithdrawalAccountType.THERA_WALLET && (
            <div className={`mt-2 text-xs font-medium ${isSelected ? 'text-neutral-600' : 'text-neutral-400'}`}>
              {method.accountType === WithdrawalAccountType.ASTROPAY ? (
                <>
                  {method.title && `${method.title} | `}{' '}
                  <PhoneInput
                    placeholder="Enter phone number"
                    value={method.details.phone}
                    international
                    readOnly
                    onChange={() => {
                      /* do nothing */
                    }}
                    className={`focus-none shadow-0 pointer-events-none border-none bg-transparent p-0 text-left text-xs font-medium ${isSelected ? 'text-neutral-600' : 'text-neutral-400'} outline-none ring-0`}
                  />
                  {showWithdrawalMethodIDToCopy && <CopyableText text={method.id} textClassName="mt-1" />}
                </>
              ) : (
                <>
                  {method.title && `${method.title} | `}{' '}
                  <span data-sensitive-copy-admins>
                    {method.details.accountNumber ||
                      method.details.bank_account ||
                      method.details.IBAN ||
                      method.details.email ||
                      method.details.phone ||
                      method.details.wallet_address ||
                      method.details.document_id}
                  </span>
                  {showWithdrawalMethodIDToCopy && <CopyableText text={method.id} textClassName="mt-1" />}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={`flex-shrink-0 pl-2 ${accountType === WithdrawalAccountType.THERA_WALLET ? 'hidden' : ''}`}>
        <div className="relative flex h-10 items-center justify-end gap-1 text-right">
          {isSettingPreferred === id ? (
            <div className="mb-1">
              <LoadingSpinner height={16} width={16} />
            </div>
          ) : (
            <>
              {needsPayoneerConnect && onConnectPayoneer && (
                <Button
                  variant="theraRedOutline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onConnectPayoneer(id);
                  }}
                  className="whitespace-nowrap px-3 py-1.5 text-xs"
                >
                  Connect Payoneer
                </Button>
              )}
              {(isHovered || isSelected) &&
                !withdrawalDetailsModal.isOpen &&
                accountType !== WithdrawalAccountType.THERA_WALLET && (
                  <Button
                    variant="default"
                    onClick={(e) => {
                      e.stopPropagation();
                      withdrawalDetailsModal.handleOpen();
                    }}
                    className="p-1"
                    aria-label="View account details"
                  >
                    <EyeIcon aria-hidden="true" className="h-5 w-5 cursor-pointer text-[#8C8C8C]" />
                  </Button>
                )}
              <TheraMenu
                menuButton={<MoreButton size="md" />}
                items={[
                  {
                    name: 'Delete',
                    handleOnClick: () => {
                      // if it is preferred, we don't want to delete it,
                      // ask the user to set another method as preferred first
                      if (isPreferred) {
                        theraToast.warning('Please set another method as preferred first.', {
                          id: 'wise-preferred-method',
                          icon: <Warning strokeColor="white" />,
                        });
                        return;
                      }
                      onDelete?.(id);
                    },
                    className: 'text-red-600',
                    isHidden: accountType === WithdrawalAccountType.THERA_WALLET,
                  },
                  { name: 'Set as Preferred', handleOnClick: () => setPreferredMethod(id), isHidden: isPreferred },
                  {
                    name: 'Make it Off Platform',
                    handleOnClick: () => onMakeOffPlatform?.(id),
                    isHidden:
                      (!isSuperAdminMode && !isSupportAdminMode) || accountType === WithdrawalAccountType.THERA_WALLET,
                  },
                  {
                    name: 'Make it On Platform',
                    handleOnClick: () => onMakeOnPlatform?.(id),
                    isHidden:
                      (!isSuperAdminMode && !isSupportAdminMode) || accountType === WithdrawalAccountType.THERA_WALLET,
                  },
                ]}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WiseWithdrawalMethodItem;
